import { Loader2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  AssetClearOverlayButton,
  AssetCloseOverlayButton,
  AssetCorrectOverlayButton,
  AssetLibraryOverlayButton,
  AssetOverlayTopLeft,
  AssetOverlayTopRight,
  AssetPromptOverlayButton,
  AssetRegenerateOverlayButton,
  AssetRoomViewerOverlayButton,
  AssetUploadOverlayButton,
  AssetVersionsBadge,
} from "@/components/media/asset-media-overlay"
import { AssetPreviewGenerating, assetPreviewGeneratingFromJob } from "@/components/media/asset-preview-busy"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { overlayThumbStripInset } from "@/components/media/overlay-thumb-strip"
import { useTranslation } from "@/hooks/use-translation"
import { REFERENCE_IMAGE_FILE_ACCEPT } from "@/lib/reference-file-policy"
import { cn } from "@/lib/utils"
import type { GenerationJobRow } from "@/types/workspace"

export type EntityMultiViewRow = {
  view: string
  url?: string | null
  thumb_url?: string | null
  display_url?: string | null
  download_url?: string | null
  active_version_id?: number | null
}

type Props = {
  views: EntityMultiViewRow[]
  busy?: boolean
  onGenerateView?: (view: string) => void
  /**
   * Stable SPA reconnect id per view (`confirmDelayKey("character", id, "portrait", view)`).
   * Required when `onGenerateView` is set.
   */
  confirmDelayKeyForView?: (view: string) => string
  viewLabel: (view: string) => string
  generateLabel: (view: string, label: string) => string
  emptyLabel: (viewLabel: string) => string
  actionButtons?: React.ReactNode
  modelLabel?: string | null
  selectedView?: string
  onViewChange?: (view: string) => void
  hideActions?: boolean
  generateDisabled?: boolean
  generateDisabledReason?: string
  onRegenerateView?: () => void
  regenerateDisabled?: boolean
  regenerateDisabledReason?: string
  regenerateLabel?: string
  viewBusy?: (view: string) => boolean
  /** The view's in-flight job behind `viewBusy` — status, stage and timer in the generating status row. */
  viewGeneratingJob?: (view: string) => GenerationJobRow | null
  hideTitle?: boolean
  title?: string
  previewHeightClass?: string
  previewObjectFit?: "cover" | "contain"
  compactThumbs?: boolean
  /** Hide view thumb strip (selection lives elsewhere, e.g. location angles section). */
  hideThumbs?: boolean
  overlayActions?: boolean
  /** Grow to fill a flex parent (Characters hero). Do not use above sibling sections like location angles. */
  fillHeight?: boolean
  versionCount?: number
  versionsTitle?: string
  onVersions?: () => void
  onUploadFile?: (file: File) => void | Promise<void>
  uploadTitle?: string
  onPickFromLibrary?: () => void
  libraryTitle?: string
  onCorrect?: () => void
  correctDisabled?: boolean
  correctDisabledReason?: string
  correctTitle?: string
  onPreviewPrompt?: () => void
  promptTitle?: string
  onOpenRoomViewer?: () => void
  roomViewerTitle?: string
  onPreview?: () => void
  queuedShortLabel?: string
  onClear?: () => void
  clearLabel?: string
}

export function EntityMultiViewThumb({
  url,
  thumbUrl,
  selected,
  busy,
  label,
  onSelect,
  onPreview,
  previewTitle,
  onRemove,
  removeTitle,
  compact,
  showCaptionBelow = false,
}: {
  url: string | null | undefined
  thumbUrl?: string | null
  selected: boolean
  busy?: boolean
  label: string
  onSelect: () => void
  onPreview?: () => void
  previewTitle?: string
  onRemove?: () => void
  removeTitle?: string
  compact?: boolean
  showCaptionBelow?: boolean
}) {
  const src = thumbUrl ?? url
  const showPreview = Boolean(onPreview && src)
  const showRemove = Boolean(onRemove)
  const chipClass = cn(compact && "p-0.5 [&_svg]:h-3 [&_svg]:w-3")

  return (
    <div className="flex shrink-0 flex-col items-center gap-1.5">
      <div
        className={cn(
          "relative overflow-hidden rounded-lg border bg-ui-surface-sunken",
          compact ? "size-11" : "size-16",
          selected ? "border-ui-accent ring-1 ring-ui-accent/40 ring-inset" : "border-ui-border",
        )}
      >
        <button type="button" onClick={onSelect} className="absolute inset-0 flex cursor-pointer" title={label}>
          {src ? (
            <img src={src} alt="" className={cn("h-full w-full object-cover", busy && "opacity-40")} />
          ) : (
            <span className="flex h-full w-full items-center justify-center px-1 text-center">
              <span
                className={cn(
                  "leading-tight text-balance text-ui-text-muted",
                  compact ? "text-[7px]" : "text-[8px]",
                  showCaptionBelow && "sr-only",
                )}
              >
                {label}
              </span>
            </span>
          )}
          {busy ? (
            <span className="absolute inset-0 z-10 flex items-center justify-center bg-ui-surface-sunken/60">
              <Loader2 className="h-4 w-4 animate-spin text-ui-text-muted" />
            </span>
          ) : null}
        </button>
        {showPreview && onPreview ? (
          <AssetOverlayTopLeft className={cn(compact && "top-0.5 left-0.5")}>
            <AssetPromptOverlayButton title={previewTitle ?? label} onClick={onPreview} className={chipClass} />
          </AssetOverlayTopLeft>
        ) : null}
        {showRemove && onRemove ? (
          <AssetOverlayTopRight className={cn(compact && "top-0.5 right-0.5")}>
            <AssetCloseOverlayButton title={removeTitle ?? label} onClick={onRemove} className={chipClass} />
          </AssetOverlayTopRight>
        ) : null}
      </div>
      {showCaptionBelow ? (
        <span className="max-w-11 truncate text-center text-[9px] leading-tight text-ui-text-muted">{label}</span>
      ) : null}
    </div>
  )
}

function ViewPreview({
  url,
  busy,
  emptyLabel,
  overlayActions,
  reserveThumbSpace,
  generateLabel,
  modelLabel,
  onGenerateView,
  confirmDelayKey,
  generateBusy,
  generateDisabled,
  generateDisabledReason,
  onPreview,
  queuedShortLabel,
  objectFit = "cover",
}: {
  url: string | null | undefined
  busy?: boolean
  emptyLabel: string
  overlayActions?: boolean
  /** Extra bottom padding so empty CTA clears overlay thumb strip. */
  reserveThumbSpace?: boolean
  generateLabel?: string
  modelLabel?: string | null
  onGenerateView?: () => void
  confirmDelayKey?: string
  generateBusy?: boolean
  generateDisabled?: boolean
  generateDisabledReason?: string
  onPreview?: () => void
  queuedShortLabel: string
  objectFit?: "cover" | "contain"
}) {
  const [imageLoading, setImageLoading] = useState(false)
  const showImageLoadSpinner = Boolean(url && imageLoading && !busy)

  useEffect(() => {
    setImageLoading(Boolean(url))
  }, [url])

  if (!url) {
    if (busy) return null
    if (overlayActions && onGenerateView && generateLabel && confirmDelayKey) {
      return (
        <div
          className={cn(
            "flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center",
            reserveThumbSpace ? "pt-8 pb-16" : "py-8",
          )}
        >
          <p className="text-sm text-ui-text-muted">{emptyLabel}</p>
          <GenerateWithModelLabel
            label={generateLabel}
            busyLabel={queuedShortLabel}
            modelLabel={modelLabel}
            busy={generateBusy}
            disabled={generateDisabled}
            disabledReason={generateDisabledReason}
            onClick={onGenerateView}
            confirmDelayKey={confirmDelayKey}
          />
        </div>
      )
    }
    return <p className="px-4 text-center text-sm text-ui-text-muted">{emptyLabel}</p>
  }

  return (
    <div className="relative h-full w-full">
      <img
        src={url}
        alt=""
        role={onPreview ? "button" : undefined}
        tabIndex={onPreview ? 0 : undefined}
        onClick={
          onPreview
            ? (e) => {
                e.stopPropagation()
                onPreview()
              }
            : undefined
        }
        onKeyDown={
          onPreview
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  e.stopPropagation()
                  onPreview()
                }
              }
            : undefined
        }
        className={cn(
          "h-full w-full",
          objectFit === "contain" ? "object-contain" : "object-cover",
          onPreview && "cursor-pointer",
          showImageLoadSpinner && "opacity-40",
        )}
        onLoad={() => setImageLoading(false)}
        onError={() => setImageLoading(false)}
      />
      {showImageLoadSpinner ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-ui-text-muted" />
        </span>
      ) : null}
    </div>
  )
}

export function entityMultiViewDisplayUrl(views: EntityMultiViewRow[], view: string) {
  const row = views.find((v) => v.view === view)
  if (!row) return null
  return row.display_url ?? row.url ?? null
}

export function EntityMultiViewMediaPanel({
  views,
  busy,
  onGenerateView,
  confirmDelayKeyForView,
  viewLabel,
  generateLabel,
  emptyLabel,
  actionButtons,
  modelLabel,
  selectedView: selectedViewProp,
  onViewChange,
  hideActions = false,
  generateDisabled = false,
  generateDisabledReason,
  onRegenerateView,
  regenerateDisabled = false,
  regenerateDisabledReason,
  regenerateLabel,
  viewBusy,
  viewGeneratingJob,
  hideTitle = false,
  title,
  previewHeightClass,
  previewObjectFit = "cover",
  compactThumbs = false,
  hideThumbs = false,
  overlayActions = false,
  fillHeight = false,
  versionCount = 0,
  versionsTitle,
  onVersions,
  onUploadFile,
  uploadTitle,
  onPickFromLibrary,
  libraryTitle,
  onCorrect,
  correctDisabled,
  correctDisabledReason,
  correctTitle,
  onPreviewPrompt,
  promptTitle,
  onOpenRoomViewer,
  roomViewerTitle,
  onPreview,
  queuedShortLabel,
  onClear,
  clearLabel,
}: Props) {
  const { t } = useTranslation(["workspace"])
  const [internalView, setInternalView] = useState(views[0]?.view ?? "")
  const [uploading, setUploading] = useState(false)
  const selectedView = selectedViewProp ?? internalView
  const queued = queuedShortLabel ?? t("charactersPage.queuedShort")

  const setSelectedView = useCallback(
    (view: string) => {
      if (onViewChange) onViewChange(view)
      else setInternalView(view)
    },
    [onViewChange],
  )

  const active = useMemo(() => views.find((v) => v.view === selectedView) ?? views[0], [views, selectedView])

  const selectedGenerateLabel = generateLabel(selectedView, viewLabel(selectedView))
  const selectedEmptyLabel = emptyLabel(viewLabel(selectedView))
  const showRegenerate = onRegenerateView != null && Boolean(active?.url)
  const showTopRight =
    overlayActions && (onOpenRoomViewer != null || onPreviewPrompt != null || onCorrect != null || showRegenerate)
  const activeViewBusy = viewBusy ? viewBusy(selectedView) : busy
  const handleUpload = async (file: File) => {
    if (!onUploadFile) return
    setUploading(true)
    try {
      await onUploadFile(file)
    } finally {
      setUploading(false)
    }
  }

  const showClear = Boolean(active?.url && onClear)
  const clearDisabled = Boolean(activeViewBusy || uploading)
  const thumbs = hideThumbs ? null : (
    <>
      {views.map((v) => (
        <EntityMultiViewThumb
          key={v.view}
          url={v.url}
          thumbUrl={v.thumb_url}
          selected={selectedView === v.view}
          busy={viewBusy ? viewBusy(v.view) : busy}
          label={viewLabel(v.view)}
          onSelect={() => setSelectedView(v.view)}
          compact={compactThumbs}
        />
      ))}
    </>
  )
  const overlayThumbsInset = overlayActions && thumbs ? overlayThumbStripInset(compactThumbs) : 0
  const generating = assetPreviewGeneratingFromJob(viewGeneratingJob?.(selectedView), {
    fallbackLabel: queued,
    modelLabel,
    insetBottom: overlayThumbsInset,
  })

  return (
    <div className={cn("flex flex-col gap-3", fillHeight && "h-full min-h-0 flex-1 justify-end")}>
      {!hideTitle && title ? (
        <p className="text-xs font-semibold tracking-wide text-ui-text-muted uppercase">{title}</p>
      ) : null}
      <div
        className={cn(fillHeight && "flex min-h-0 flex-1 flex-col justify-end", !fillHeight && "flex flex-col gap-2")}
      >
        {!overlayActions && thumbs ? (
          compactThumbs ? (
            <div className="it2tv-scrollbar -mx-0.5 overflow-x-auto px-0.5 pb-1">
              <div className="flex w-max min-w-full justify-center gap-1">{thumbs}</div>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-1.5 pb-1">{thumbs}</div>
          )
        ) : null}
        <div
          className={cn(
            "relative flex items-center justify-center overflow-hidden rounded-lg border bg-ui-surface-sunken",
            !active?.url && overlayActions && "border-dashed border-ui-border-strong",
            active?.url && "border-ui-border",
            previewHeightClass ?? "min-h-[220px] flex-1",
          )}
        >
          <ViewPreview
            url={entityMultiViewDisplayUrl(views, selectedView)}
            busy={activeViewBusy}
            emptyLabel={selectedEmptyLabel}
            overlayActions={overlayActions}
            reserveThumbSpace={Boolean(overlayActions && thumbs)}
            generateLabel={selectedGenerateLabel}
            modelLabel={modelLabel}
            onGenerateView={onGenerateView ? () => onGenerateView(selectedView) : undefined}
            confirmDelayKey={confirmDelayKeyForView?.(selectedView)}
            generateBusy={activeViewBusy}
            generateDisabled={generateDisabled}
            generateDisabledReason={generateDisabledReason}
            onPreview={onPreview}
            queuedShortLabel={queued}
            objectFit={previewObjectFit}
          />
          <AssetPreviewGenerating
            active={Boolean(activeViewBusy)}
            onMedia={Boolean(active?.url)}
            status={generating.status}
            caption={generating.caption}
          />
          {overlayActions && thumbs ? (
            <div className="absolute inset-x-0 bottom-3 z-10 px-2">
              <div className="it2tv-scrollbar -mx-0.5 overflow-x-auto px-0.5">
                <div className="flex w-max min-w-full justify-center gap-1">{thumbs}</div>
              </div>
            </div>
          ) : null}
          {overlayActions && (onVersions || onUploadFile || onPickFromLibrary || showClear) ? (
            <AssetOverlayTopLeft>
              {onVersions ? (
                <AssetVersionsBadge count={versionCount} title={versionsTitle ?? ""} onOpen={onVersions} />
              ) : null}
              {onUploadFile ? (
                <AssetUploadOverlayButton
                  accept={REFERENCE_IMAGE_FILE_ACCEPT}
                  uploading={uploading}
                  title={uploadTitle ?? ""}
                  onFile={handleUpload}
                />
              ) : null}
              {onPickFromLibrary ? (
                <AssetLibraryOverlayButton
                  title={libraryTitle ?? t("canvas.referencesModal.pickFromLibrary")}
                  onClick={onPickFromLibrary}
                />
              ) : null}
              {showClear && onClear ? (
                <AssetClearOverlayButton
                  label={clearLabel}
                  disabled={clearDisabled}
                  disabledReason={clearDisabled ? queued : undefined}
                  onClear={onClear}
                />
              ) : null}
            </AssetOverlayTopLeft>
          ) : null}
          {showTopRight ? (
            <AssetOverlayTopRight className="z-30">
              {onOpenRoomViewer ? (
                <AssetRoomViewerOverlayButton title={roomViewerTitle ?? ""} onClick={onOpenRoomViewer} />
              ) : null}
              {onPreviewPrompt ? (
                <AssetPromptOverlayButton title={promptTitle ?? ""} onClick={onPreviewPrompt} />
              ) : null}
              {onCorrect ? (
                <AssetCorrectOverlayButton
                  title={correctTitle ?? ""}
                  disabled={correctDisabled}
                  disabledReason={correctDisabledReason}
                  onClick={onCorrect}
                />
              ) : null}
              {showRegenerate ? (
                <AssetRegenerateOverlayButton
                  title={regenerateLabel ?? ""}
                  disabled={regenerateDisabled}
                  disabledReason={regenerateDisabledReason}
                  busy={activeViewBusy}
                  onClick={onRegenerateView}
                />
              ) : null}
            </AssetOverlayTopRight>
          ) : null}
        </div>
      </div>
      {!hideActions && onGenerateView && confirmDelayKeyForView ? (
        <div className="flex flex-wrap items-start gap-2">
          <GenerateWithModelLabel
            label={selectedGenerateLabel}
            busyLabel={queued}
            modelLabel={modelLabel}
            busy={activeViewBusy}
            disabled={generateDisabled}
            disabledReason={generateDisabledReason}
            onClick={() => onGenerateView(selectedView)}
            confirmDelayKey={confirmDelayKeyForView(selectedView)}
          />
          {actionButtons}
        </div>
      ) : null}
    </div>
  )
}
